import React, { useState, useEffect } from 'react';
import { getCurrentUser } from '../services/authService';
import { saveReceipt, Receipt, getCarriers, Carrier, getLanes, Lane } from '../services/dbService';
import { extractBolNumber, extractPdfData } from '../services/emailService';
import { generatePdfReceipt } from '../services/receiptService';
import { PDFDocument } from 'pdf-lib';
import { debugLogPdfContent } from '../services/debugService';

interface BatchFile {
  id: string; // unique identifier for this file in the batch
  file: File | null; // Can be null for manual entries
  bolNumber: string;
  shipmentNumber: string;
  extractedBol: string | null;
  extractedShipment: string | null;
  processed: boolean;
  isProcessing: boolean;
  error: string | null;
  isManualEntry: boolean;
  pdfPreviewUrl: string | null;
  origin: string;
  destination: string;
  amount: number;
  date: Date;
  status: 'pending' | 'paid' | 'cancelled';
}

const BatchReceipt: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [lanes, setLanes] = useState<Lane[]>([]);
  const [selectedLane, setSelectedLane] = useState<string>('');
  const [selectedCarrierId, setSelectedCarrierId] = useState<string>('');
  const [selectedCarrierName, setSelectedCarrierName] = useState<string>('');
  const [batchFiles, setBatchFiles] = useState<BatchFile[]>([]);
  const [processingCount, setProcessingCount] = useState<number>(0);
  const [successCount, setSuccessCount] = useState<number>(0);
  const [previewPanelOpen, setPreviewPanelOpen] = useState<boolean>(false);
  const [selectedPreviewId, setSelectedPreviewId] = useState<string | null>(null);
  
  // Form state for default values
  const [defaultValues, setDefaultValues] = useState({
    origin: '',
    destination: '',
    amount: 140, // Default amount
    date: new Date(),
    status: 'pending' as 'pending' | 'paid' | 'cancelled',
  });
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [fetchedCarriers, fetchedLanes] = await Promise.all([
          getCarriers(),
          getLanes()
        ]);
        setCarriers(fetchedCarriers);
        setLanes(fetchedLanes);
      } catch (err: any) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const handleCarrierChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const carrierId = e.target.value;
    setSelectedCarrierId(carrierId);
    
    if (carrierId) {
      const carrier = carriers.find(c => c.id === carrierId);
      if (carrier) {
        setSelectedCarrierName(carrier.name);
      }
    } else {
      setSelectedCarrierName('');
    }
  };
  
  const handleLaneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const laneId = e.target.value;
    setSelectedLane(laneId);
    
    if (laneId) {
      const lane = lanes.find(l => l.id === laneId);
      if (lane) {
        setDefaultValues({
          ...defaultValues,
          origin: lane.origin,
          destination: lane.destination,
          amount: lane.price
        });
      }
    }
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setLoading(true);
      const newFiles: BatchFile[] = [];
      
      for (let i = 0; i < e.target.files.length; i++) {
        const file = e.target.files[i];
        newFiles.push({
          id: `file-${Date.now()}-${i}`,
          file,
          bolNumber: '',
          shipmentNumber: '',
          extractedBol: null,
          extractedShipment: null,
          processed: false,
          isProcessing: false,
          error: null,
          isManualEntry: false,
          pdfPreviewUrl: null,
          origin: defaultValues.origin,
          destination: defaultValues.destination,
          amount: defaultValues.amount,
          date: defaultValues.date,
          status: defaultValues.status
        });
      }
      
      setBatchFiles([...batchFiles, ...newFiles]);
      
      // Reset file input
      e.target.value = '';
      
      // Process each file to extract information
      for (let i = 0; i < newFiles.length; i++) {
        const fileData = newFiles[i];
        await processFile(fileData);
      }
      
      setLoading(false);
    }
  };
  
  const processFile = async (fileData: BatchFile) => {
    // Skip if no file
    if (!fileData.file) {
      return;
    }
    
    // Update file processing status
    setBatchFiles(prev => 
      prev.map(f => f.id === fileData.id ? { ...f, isProcessing: true } : f)
    );
    
    try {
      setProcessingCount(prev => prev + 1);
      
      console.log('---DEBUG: PDF PROCESSING START---');
      console.log('File name:', fileData.file.name);
      console.log('File size:', fileData.file.size, 'bytes');
      
      // Read the PDF file
      const fileBuffer = await fileData.file.arrayBuffer();
      const pdfBytes = new Uint8Array(fileBuffer);
      
      // Run debug logging
      await debugLogPdfContent(pdfBytes);
      
      // Extract data from PDF using enhanced function
      const pdfData = await extractPdfData(pdfBytes);
      console.log('Extracted PDF data:', pdfData);
      
      // Get BOL number and other data
      const bol = pdfData.bolNumber;
      console.log('Extracted BOL:', bol || 'None found');
      
      // Try to extract shipment number from PDF content or filename if not found
      let shipmentNumber = pdfData.shipmentNumber || '';
      console.log('Extracted shipment number:', shipmentNumber || 'None found');
      
      if (!shipmentNumber) {
        const fileNameMatch = fileData.file.name.match(/(\d{7,10})/);
        if (fileNameMatch) {
          shipmentNumber = fileNameMatch[0];
          console.log('Extracted shipment number from filename:', shipmentNumber);
        }
      }
      
      // Create PDF preview URL
      const blob = new Blob([fileBuffer], { type: 'application/pdf' });
      const pdfPreviewUrl = URL.createObjectURL(blob);
      
      // Check if we're using origin/destination from a lane
      const laneSelected = selectedLane !== '';
      
      if (pdfData.origin) {
        console.log(laneSelected ? 'Lane selected, ignoring extracted origin:' : 'Origin:', pdfData.origin);
      } else {
        console.log('No origin found in PDF');
      }
      
      if (pdfData.destination) {
        console.log(laneSelected ? 'Lane selected, ignoring extracted destination:' : 'Destination:', pdfData.destination);
      } else {
        console.log('No destination found in PDF');
      }
      
      console.log('Delivery date:', pdfData.deliveryDate?.toISOString() || 'None found');
      console.log('---DEBUG: PDF PROCESSING END---');
      
      // Update the file data with extracted information
      // But preserve origin/destination if a lane is selected
      setBatchFiles(prev => 
        prev.map(f => {
          if (f.id === fileData.id) {
            return {
              ...f,
              extractedBol: bol,
              bolNumber: bol || '',
              extractedShipment: shipmentNumber,
              shipmentNumber: shipmentNumber,
              // Only update origin/destination if a lane isn't selected
              origin: laneSelected ? f.origin : (pdfData.origin || f.origin),
              destination: laneSelected ? f.destination : (pdfData.destination || f.destination),
              date: pdfData.deliveryDate || f.date,
              isProcessing: false,
              processed: true,
              pdfPreviewUrl
            };
          }
          return f;
        })
      );
      
      setSuccessCount(prev => prev + 1);
    } catch (err: any) {
      console.error('Error processing PDF', err);
      
      // Update file with error
      setBatchFiles(prev => 
        prev.map(f => {
          if (f.id === fileData.id) {
            return {
              ...f,
              isProcessing: false,
              error: err.message || 'Failed to process PDF'
            };
          }
          return f;
        })
      );
    } finally {
      setProcessingCount(prev => prev - 1);
    }
  };
  
  const updateBatchFile = (id: string, data: Partial<BatchFile>) => {
    setBatchFiles(prev => 
      prev.map(f => f.id === id ? { ...f, ...data } : f)
    );
  };
  
  const removeFile = (id: string) => {
    // Revoke URL if it exists to prevent memory leaks
    const file = batchFiles.find(f => f.id === id);
    if (file && file.pdfPreviewUrl) {
      URL.revokeObjectURL(file.pdfPreviewUrl);
    }
    
    setBatchFiles(prev => prev.filter(f => f.id !== id));
    
    // Close preview if the removed file was being previewed
    if (selectedPreviewId === id) {
      setSelectedPreviewId(null);
      setPreviewPanelOpen(false);
    }
  };
  
  const addManualEntry = () => {
    const newFile: BatchFile = {
      id: `manual-${Date.now()}`,
      file: null,
      bolNumber: '',
      shipmentNumber: '',
      extractedBol: null,
      extractedShipment: null,
      processed: false,
      isProcessing: false,
      error: null,
      isManualEntry: true,
      pdfPreviewUrl: null,
      origin: defaultValues.origin,
      destination: defaultValues.destination,
      amount: defaultValues.amount,
      date: defaultValues.date,
      status: defaultValues.status
    };
    
    setBatchFiles([...batchFiles, newFile]);
  };
  
  const generateAllReceipts = async () => {
    if (!selectedCarrierId) {
      setError('Please select a carrier first');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const user = getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      let successfulReceipts = 0;
      
      // Process each file to generate and save receipt
      for (const fileData of batchFiles) {
        // Skip files without BOL number
        if (!fileData.bolNumber) {
          updateBatchFile(fileData.id, { 
            error: 'BOL number is required'
          });
          continue;
        }
        
        try {
          // Create the receipt
          const receipt: Receipt = {
            userId: user.uid,
            carrierId: selectedCarrierId,
            carrierName: selectedCarrierName,
            laneId: selectedLane || undefined,
            origin: fileData.origin,
            destination: fileData.destination,
            date: fileData.date,
            bolNumber: fileData.bolNumber,
            shipmentNumber: fileData.shipmentNumber,
            amount: fileData.amount,
            status: fileData.status,
            pdfFilename: fileData.file?.name,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          
          // Save to database
          await saveReceipt(receipt);
          
          // Update status
          updateBatchFile(fileData.id, { 
            processed: true,
            error: null
          });
          
          successfulReceipts++;
        } catch (err: any) {
          console.error('Error saving receipt:', err);
          updateBatchFile(fileData.id, { 
            error: err.message || 'Failed to save receipt'
          });
        }
      }
      
      if (successfulReceipts > 0) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
      
      if (successfulReceipts === batchFiles.length) {
        // All receipts were successful, clear the batch
        // Revoke any object URLs first
        batchFiles.forEach(file => {
          if (file.pdfPreviewUrl) {
            URL.revokeObjectURL(file.pdfPreviewUrl);
          }
        });
        setBatchFiles([]);
        setPreviewPanelOpen(false);
        setSelectedPreviewId(null);
      }
    } catch (err: any) {
      console.error('Error in batch processing:', err);
      setError(err.message || 'Failed to process batch');
    } finally {
      setLoading(false);
    }
  };
  
  const previewFile = (id: string) => {
    setSelectedPreviewId(id);
    setPreviewPanelOpen(true);
  };
  
  const downloadPreview = async () => {
    if (!selectedCarrierId) {
      setError('Please select a carrier first');
      return;
    }
    
    try {
      setLoading(true);
      
      const user = getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      if (batchFiles.length === 0) {
        throw new Error('No files to preview');
      }
      
      // Select first file with BOL number for preview
      const fileData = batchFiles.find(f => f.bolNumber) || batchFiles[0];
      
      if (!fileData.bolNumber) {
        throw new Error('BOL number is required for preview');
      }
      
      // Create a temporary receipt for preview
      const receipt: Receipt = {
        userId: user.uid,
        carrierId: selectedCarrierId,
        carrierName: selectedCarrierName,
        laneId: selectedLane || undefined,
        origin: fileData.origin,
        destination: fileData.destination,
        date: fileData.date,
        bolNumber: fileData.bolNumber,
        shipmentNumber: fileData.shipmentNumber,
        amount: fileData.amount,
        status: fileData.status,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Generate and download the PDF
      const pdfBytes = await generatePdfReceipt(receipt);
      
      // Create blob and download
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `Preview_Receipt_${receipt.bolNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error generating preview:', err);
      setError(err.message || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };
  
  // Function to apply default values to a specific batch file
  const applyDefaultsToFile = (fileId: string) => {
    updateBatchFile(fileId, {
      origin: defaultValues.origin,
      destination: defaultValues.destination,
      amount: defaultValues.amount,
      date: defaultValues.date,
      status: defaultValues.status
    });
  };
  
  // Cleanup effect to revoke object URLs when component unmounts
  useEffect(() => {
    return () => {
      batchFiles.forEach(file => {
        if (file.pdfPreviewUrl) {
          URL.revokeObjectURL(file.pdfPreviewUrl);
        }
      });
    };
  }, []);
  
  return (
    <div className="batch-receipt-page">
      <div className="page-header">
        <h1>NFI Lumper Receipt Creator | Batch Receipt Processing</h1>
      </div>
      
      {loading && <div className="loading">Processing...</div>}
      
      {error && <div className="error-message">{error}</div>}
      
      {success && (
        <div className="success-message">
          Receipts saved successfully!
        </div>
      )}
      
      <div className="batch-container">
        <div className={`batch-main-content ${previewPanelOpen ? 'with-preview' : ''}`}>
          <div className="form-section">
            <h2>1. Select Carrier and Lane</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="carrierId">Carrier*</label>
                <select
                  id="carrierId"
                  value={selectedCarrierId}
                  onChange={handleCarrierChange}
                  required
                  aria-label="Select carrier for receipts"
                >
                  <option value="">Select Carrier</option>
                  {carriers.map(carrier => (
                    <option key={carrier.id} value={carrier.id}>
                      {carrier.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="lane">Lane (Optional)</label>
                <select
                  id="lane"
                  value={selectedLane}
                  onChange={handleLaneChange}
                  aria-label="Select lane to auto-fill receipt information"
                  title="Select a lane to auto-fill origin, destination, and price"
                >
                  <option value="">Select Lane</option>
                  {lanes.map(lane => (
                    <option key={lane.id} value={lane.id}>
                      {lane.origin} → {lane.destination} (${lane.price})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2>2. Default Receipt Values</h2>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="date">Date</label>
                <input
                  type="date"
                  id="date"
                  value={defaultValues.date instanceof Date 
                    ? defaultValues.date.toISOString().split('T')[0]
                    : new Date().toISOString().split('T')[0]}
                  onChange={(e) => setDefaultValues({
                    ...defaultValues,
                    date: new Date(e.target.value)
                  })}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="amount">Amount ($)</label>
                <input
                  type="number"
                  id="amount"
                  value={defaultValues.amount}
                  onChange={(e) => setDefaultValues({
                    ...defaultValues,
                    amount: parseFloat(e.target.value)
                  })}
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="origin">Origin</label>
                <input
                  type="text"
                  id="origin"
                  value={defaultValues.origin}
                  onChange={(e) => setDefaultValues({
                    ...defaultValues,
                    origin: e.target.value
                  })}
                  placeholder="Origin"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="destination">Destination</label>
                <input
                  type="text"
                  id="destination"
                  value={defaultValues.destination}
                  onChange={(e) => setDefaultValues({
                    ...defaultValues,
                    destination: e.target.value
                  })}
                  placeholder="Destination"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={defaultValues.status}
                  onChange={(e) => setDefaultValues({
                    ...defaultValues,
                    status: e.target.value as 'pending' | 'paid' | 'cancelled'
                  })}
                  required
                  aria-label="Select default receipt status"
                  title="Select the default payment status for receipts"
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h2>3. Add Loads to Batch</h2>
            <div className="add-entries-container">
              <div className="option-box">
                <h3>Upload PDF Files</h3>
                <p>Upload PDF files to automatically extract BOL and shipment numbers</p>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  multiple
                  className="file-input"
                  title="Select PDF files to upload"
                  aria-label="Select PDF files to upload"
                />
              </div>
              
              <div className="option-divider">OR</div>
              
              <div className="option-box">
                <h3>Add Manual Entry</h3>
                <p>Manually enter BOL and shipment numbers</p>
                <button
                  type="button"
                  onClick={addManualEntry}
                  className="add-manual-btn"
                >
                  Add Manual Entry
                </button>
              </div>
            </div>
          </div>
          
          {batchFiles.length > 0 && (
            <div className="form-section">
              <h2>4. Batch Loads</h2>
              <p className="batch-stats">
                {batchFiles.length} loads | {processingCount} processing | {successCount} processed
              </p>
              
              <div className="batch-table-container">
                <table className="batch-table">
                  <thead>
                    <tr>
                      <th>Load Information</th>
                      <th>BOL Number*</th>
                      <th>Shipment Number</th>
                      <th>Origin</th>
                      <th>Destination</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchFiles.map(file => (
                      <tr key={file.id} className={file.error ? 'has-error' : ''}>
                        <td className="file-name-cell">
                          {file.isManualEntry ? (
                            <span className="manual-entry-label">Manual Entry</span>
                          ) : file.file ? (
                            file.file.name
                          ) : "Unknown file"}
                          {file.error && (
                            <div className="file-error">{file.error}</div>
                          )}
                        </td>
                        <td>
                          <input
                            type="text"
                            value={file.bolNumber}
                            onChange={(e) => updateBatchFile(file.id, { bolNumber: e.target.value })}
                            placeholder="BOL Number"
                            aria-label={`BOL Number for ${file.isManualEntry ? 'manual entry' : file.file?.name || 'this load'}`}
                            required
                          />
                          {file.extractedBol && !file.error && (
                            <div className="extraction-result">
                              <span className="success">✓</span> Extracted
                            </div>
                          )}
                        </td>
                        <td>
                          <input
                            type="text"
                            value={file.shipmentNumber}
                            onChange={(e) => updateBatchFile(file.id, { shipmentNumber: e.target.value })}
                            placeholder="Shipment Number"
                            aria-label={`Shipment Number for ${file.isManualEntry ? 'manual entry' : file.file?.name || 'this load'}`}
                          />
                          {file.extractedShipment && !file.error && (
                            <div className="extraction-result">
                              <span className="success">✓</span> Extracted
                            </div>
                          )}
                        </td>
                        <td>
                          <input
                            type="text"
                            value={file.origin}
                            onChange={(e) => updateBatchFile(file.id, { origin: e.target.value })}
                            placeholder="Origin"
                            aria-label={`Origin for ${file.isManualEntry ? 'manual entry' : file.file?.name || 'this load'}`}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={file.destination}
                            onChange={(e) => updateBatchFile(file.id, { destination: e.target.value })}
                            placeholder="Destination"
                            aria-label={`Destination for ${file.isManualEntry ? 'manual entry' : file.file?.name || 'this load'}`}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={file.amount}
                            onChange={(e) => updateBatchFile(file.id, { amount: parseFloat(e.target.value) })}
                            placeholder="Amount"
                            step="0.01"
                            min="0"
                            aria-label={`Amount for ${file.isManualEntry ? 'manual entry' : file.file?.name || 'this load'}`}
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            value={file.date instanceof Date 
                              ? file.date.toISOString().split('T')[0]
                              : new Date().toISOString().split('T')[0]}
                            onChange={(e) => updateBatchFile(file.id, { date: new Date(e.target.value) })}
                            aria-label={`Date for ${file.isManualEntry ? 'manual entry' : file.file?.name || 'this load'}`}
                          />
                        </td>
                        <td>
                          <select
                            value={file.status}
                            onChange={(e) => updateBatchFile(file.id, { 
                              status: e.target.value as 'pending' | 'paid' | 'cancelled'
                            })}
                            aria-label={`Status for ${file.isManualEntry ? 'manual entry' : file.file?.name || 'this load'}`}
                          >
                            <option value="pending">Pending</option>
                            <option value="paid">Paid</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                          <div className="processing-status">
                            {file.isProcessing ? (
                              <span className="status-processing">Processing...</span>
                            ) : file.error ? (
                              <span className="status-error">Error</span>
                            ) : file.processed ? (
                              <span className="status-success">Processed</span>
                            ) : (
                              <span className="status-ready">Ready</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons">
                            {file.pdfPreviewUrl && (
                              <button
                                type="button"
                                onClick={() => previewFile(file.id)}
                                className="preview-file-btn"
                                aria-label={`Preview ${file.file?.name || 'file'}`}
                              >
                                Preview
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeFile(file.id)}
                              className="remove-file-btn"
                              aria-label={`Remove ${file.isManualEntry ? 'manual entry' : file.file?.name || 'this load'}`}
                            >
                              Remove
                            </button>
                            <button
                              type="button"
                              onClick={() => applyDefaultsToFile(file.id)}
                              className="apply-defaults-btn"
                              aria-label={`Apply defaults to ${file.isManualEntry ? 'manual entry' : file.file?.name || 'this load'}`}
                            >
                              Apply Defaults
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {batchFiles.length > 0 && (
            <div className="form-actions">
              <button
                type="button"
                onClick={downloadPreview}
                className="preview-btn"
                disabled={loading || !selectedCarrierId}
              >
                Preview Sample Receipt
              </button>
              <button
                type="button"
                onClick={generateAllReceipts}
                className="save-btn"
                disabled={loading || !selectedCarrierId}
              >
                Generate All Receipts
              </button>
            </div>
          )}
        </div>
        
        {previewPanelOpen && selectedPreviewId && (
          <div className="pdf-preview-panel">
            <div className="preview-header">
              <h3>PDF Preview</h3>
              <button 
                onClick={() => setPreviewPanelOpen(false)} 
                className="close-preview-btn"
                aria-label="Close preview"
              >
                ×
              </button>
            </div>
            {(() => {
              const file = batchFiles.find(f => f.id === selectedPreviewId);
              if (file && file.pdfPreviewUrl) {
                return (
                  <iframe 
                    src={file.pdfPreviewUrl} 
                    title="PDF Preview" 
                    className="pdf-iframe"
                  ></iframe>
                );
              }
              return <div className="no-preview">No preview available</div>;
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchReceipt; 